import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Typography,
  makeStyles,
  createStyles,
  Button,
  Card,
  CardActionArea,
  Divider,
  Chip,
} from "@material-ui/core";
import { useHistory, useLocation } from "react-router-dom";
import ArrowRightIcon from "@material-ui/icons/ChevronRight";
import CheckIcon from "@material-ui/icons/CheckCircleOutlineSharp";

import { get日付 } from "../../helper/JapanDate";
import { getCurrentPosition, isNear } from "../../helper/Geolocation";
import { beginService, endService } from "../../helper/Api";
import {
  delayPush,
  isIllegalOparation,
  checkDoneItem,
} from "../../helper/Util";
import { errors, getApiError, getGeoError } from "../../helper/Error";
import {
  isAnywhare,
  isDisabledButtonDefault,
  isGeoUnregistered,
  shouldWatchPosition,
} from "../../helper/Option";
import TopBar from "../common/TopBar";
import ConfirmDialog from "../common/ConfirmDialog";
import AlertDialog from "../common/AlertDialog";
import ErrorDialog from "../common/ErrorDialog";
import Loading from "../common/Loading";
import TabDialog from "../common/TabDialog";
import Customer from "./Customer";
import Note from "./Note";
import Order from "./Order";
import Manual from "./Manual";

// CSSスタイルを定義
const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "left",
      margin: theme.spacing(4),
    },
    inputButton: {
      width: "100%",
      maxWidth: "600px",
      marginTop: theme.spacing(2),
    },
    flashMsg: {
      height: theme.spacing(10),
      width: "100%",
      maxWidth: "600px",
      display: "flex",
      justifyContent: "center",
      alignItems: "flex-end",
    },
    card: {
      width: "100%",
      maxWidth: "600px",
    },
    cardActionArea: {
      width: "100%",
      maxWidth: "600px",
      padding: 0,
      display: "flex",
      justifyContent: "space-between",
    },
    cardLeft: {
      paddingLeft: theme.spacing(2.5),
      paddingRight: theme.spacing(1.4),
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      width: "100%",
    },
    cardContent: {
      marginBottom: theme.spacing(1),
    },
    serviceButtons: {
      width: "100%",
      maxWidth: "600px",
      display: "flex",
      justifyContent: "space-between",
      marginTop: theme.spacing(1),
    },
    serviceButton: {
      width: "48%",
      whiteSpace: "pre-line",
    },
    chip: {
      fontSize: 15,
    },
    doneChip: {
      fontSize: 15,
      backgroundColor: theme.palette.success.main,
      color: "white",
      fontWeight: 550,
    },
    doneCheck: {
      color: "white",
    },
    chips: {
      display: "flex",
      alignItems: "center",
      marginTop: theme.spacing(1),
      minHeight: 32,
    },
    realTime: {
      marginLeft: theme.spacing(1),
    },
    dashed: {
      borderTop: "dashed 1px silver",
      backgroundColor: theme.palette.background.paper,
    },
  })
);

const beginendStatus = {
  do: 0,
  doing: 1,
  done: 2,
};

export default function Detail() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const {
    selectedName,
    selectedDate,
    timeRange,
    serviceName,
    serviceId,
    beginend,
    options,
    position,
    state,
    kirokuBeginTime,
    kirokuEndTime,
  } = location.state ?? 0;

  const errorRef = useRef({});
  const [isOpenError, setIsOpenError] = useState(false);
  const handleCloseError = useCallback(() => {
    setIsOpenError(false);

    if (errorRef.current === errors.APP_ILLEGAL_OPERATION) {
      history.push("/calendar");
    }
  }, [history]);
  const [isDisabledBtn, setIsDisabledBtn] = useState(
    isDisabledButtonDefault(options, selectedDate)
  );
  const [flashMsg, setFlashMsg] = useState("");
  const watchIdRef = useRef(null);

  const watchPosition = useCallback(() => {
    const { geolocation } = navigator;

    const settingPos = {
      latitude: position.split(",")[0],
      longitude: position.split(",")[1],
    };

    const watchSuccess = (pos) => {
      const currentPos = {
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
      };

      if (isNear(currentPos, settingPos)) {
        setIsDisabledBtn(false);
        setFlashMsg("利用者宅の近くにいます");
      } else {
        setIsDisabledBtn(true);
        setFlashMsg("利用者宅から離れています");
      }
    };

    const watchError = (err) => {
      setIsDisabledBtn(true);
      setFlashMsg("位置情報が取得できません");
    };

    const watchOption = {
      timeout: 30000, // タイムアウト時間30秒
      enableHighAccuracy: true, // 精度の高い位置情報を取得
      maximumAge: 10000, // 10秒毎に新しい値
    };

    if (geolocation) {
      watchIdRef.current = geolocation.watchPosition(
        watchSuccess,
        watchError,
        watchOption
      );
    }
  }, [position]);

  const clearWatchPosition = useCallback(() => {
    const { geolocation } = navigator;

    if (watchIdRef.current !== null && geolocation) {
      geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (isIllegalOparation(location)) {
      errorRef.current = errors.APP_ILLEGAL_OPERATION;
      setIsOpenError(true);
    }

    if (isGeoUnregistered(options, position)) {
      errorRef.current = errors.GEO_UNREGISTERED;
      setIsOpenError(true);
    } else if (shouldWatchPosition(position, options, selectedDate)) {
      setFlashMsg("現在地を取得中...");
      watchPosition();
    }

    // cleanup関数
    return () => clearWatchPosition();
  }, [
    location,
    watchPosition,
    position,
    selectedDate,
    options,
    clearWatchPosition,
  ]);

  const [beginEndBtn, setBeginEndBtn] = useState(beginend);

  const isDisabledBegin = useCallback(() => {
    switch (beginEndBtn) {
      case beginendStatus.do:
        return false;
      case beginendStatus.doing:
        return false;
      case beginendStatus.done:
        return true;
      default:
        return true;
    }
  }, [beginEndBtn]);

  const isDisabledEnd = useCallback(() => {
    switch (beginEndBtn) {
      case beginendStatus.do:
        return true;
      case beginendStatus.doing:
        return false;
      case beginendStatus.done:
        return false;
      default:
        return true;
    }
  }, [beginEndBtn]);

  const [isOpenResult, setIsOpenResult] = useState(false);
  const resultMsgRef = useRef("");
  const BEGEN_MSG = "サービスが開始されました";
  const END_MSG = "サービスが終了されました";
  const BEGEN_CANCEL_MSG = "サービス開始処理を取消しました";
  const END_CANCEL_MSG = "サービス終了処理を取消しました";

  const [isLoading, setIsLoading] = useState(false);
  const 緯度Ref = useRef(null);
  const 経度Ref = useRef(null);

  const beginServiceWrap = useCallback(
    async (cancel = false) => {
      await beginService(serviceId, 緯度Ref.current, 経度Ref.current, cancel)
        .then((res) => {
          if (res.data.error.length === 0) {
            resultMsgRef.current = cancel ? BEGEN_CANCEL_MSG : BEGEN_MSG;
            setIsOpenResult(true);
            kirokuTimeRef.current = {
              ...kirokuTimeRef.current,
              begin: res.data.result.kiroku_begintime,
            };
          } else throw res.data.error;
        })
        .catch((err) => {
          errorRef.current = err.code
            ? getApiError(err)
            : errors.APP_CONNECTION_FAILED;
          setIsOpenError(true);
        });

      setIsLoading(false);
    },
    [serviceId]
  );

  const endServiceWrap = useCallback(
    async (cancel = false) => {
      await endService(serviceId, 緯度Ref.current, 経度Ref.current, cancel)
        .then((res) => {
          if (res.data.error.length === 0) {
            resultMsgRef.current = cancel ? END_CANCEL_MSG : END_MSG;
            setIsOpenResult(true);
            kirokuTimeRef.current = {
              ...kirokuTimeRef.current,
              end: res.data.result.kiroku_endtime,
            };
          } else throw res.data.error;
        })
        .catch((err) => {
          errorRef.current = err.code
            ? getApiError(err)
            : errors.APP_CONNECTION_FAILED;

          setIsOpenError(true);
        });

      setIsLoading(false);
    },
    [serviceId]
  );

  const [isOpenConfirm, setIsOpenConfirm] = useState(false);
  const [isOpenConfirmRevoke, setIsOpenConfirmRevoke] = useState(false);

  const handleClickOk = useCallback(
    (callback) => {
      setIsOpenConfirm(false);

      setIsLoading(true);

      clearWatchPosition();

      getCurrentPosition()
        .then((pos) => {
          緯度Ref.current = pos.coords.latitude;
          経度Ref.current = pos.coords.longitude;

          callback();
        })
        .catch((err) => {
          // オプションが「どこでも」であれば位置情報なしで処理続行
          if (isAnywhare(options)) {
            緯度Ref.current = null;
            経度Ref.current = null;

            callback();

            return;
          }

          setIsLoading(false);
          errorRef.current = getGeoError(err.code);
          setIsOpenError(true);
        });
    },
    [clearWatchPosition, options]
  );

  const handleClickRevoke = useCallback(() => {
    setIsOpenConfirmRevoke(false);

    setIsLoading(true);

    clearWatchPosition();

    if (beginEndBtn === beginendStatus.doing) {
      beginServiceWrap(true);
    } else if (beginEndBtn === beginendStatus.done) {
      endServiceWrap(true);
    } else {
      setIsLoading(false);
      throw new Error();
    }
  }, [beginEndBtn, beginServiceWrap, endServiceWrap, clearWatchPosition]);

  const handleClickBeginService = () => {
    if (beginEndBtn === beginendStatus.doing) {
      setIsOpenConfirmRevoke(true);
      return;
    }
    setIsOpenConfirm(true);
  };
  const handleClickEndService = () => {
    if (beginEndBtn === beginendStatus.done) {
      setIsOpenConfirmRevoke(true);
      return;
    }
    setIsOpenConfirm(true);
  };

  const handleCloseResult = useCallback(() => {
    setIsOpenResult(false);

    if (shouldWatchPosition(position, options, selectedDate)) {
      setFlashMsg("現在地を取得中...");
      watchPosition();
    }

    setKirokuTime(kirokuTimeRef.current);

    if (resultMsgRef.current === BEGEN_MSG) {
      setBeginEndBtn(beginendStatus.doing);
      history.replace({
        pathname: history.location.pathname,
        state: {
          ...location.state,
          beginend: beginendStatus.doing,
        },
      });
    } else if (resultMsgRef.current === END_MSG) {
      setBeginEndBtn(beginendStatus.done);
      history.replace({
        pathname: history.location.pathname,
        state: {
          ...location.state,
          state: "予定通り実施",
          beginend: beginendStatus.done,
        },
      });
    } else if (resultMsgRef.current === BEGEN_CANCEL_MSG) {
      setBeginEndBtn(beginendStatus.do);
      history.replace({
        pathname: history.location.pathname,
        state: {
          ...location.state,
          beginend: beginendStatus.do,
        },
      });
    } else if (resultMsgRef.current === END_CANCEL_MSG) {
      setBeginEndBtn(beginendStatus.doing);
      history.replace({
        pathname: history.location.pathname,
        state: {
          ...location.state,
          state: "未実施",
          beginend: beginendStatus.doing,
        },
      });
    }
  }, [history, location.state, options, position, selectedDate, watchPosition]);

  const getBeginButtonColor = useCallback(
    () => (beginEndBtn === beginendStatus.doing ? "default" : "primary"),
    [beginEndBtn]
  );

  const getEndButtonColor = useCallback(
    () => (beginEndBtn === beginendStatus.done ? "default" : "primary"),
    [beginEndBtn]
  );

  const getBeginButtonText = useCallback(
    () => (beginEndBtn === beginendStatus.doing ? "開始取消し" : "開始"),
    [beginEndBtn]
  );

  const getEndButtonText = useCallback(
    () => (beginEndBtn === beginendStatus.done ? "終了取消し" : "終了"),
    [beginEndBtn]
  );

  const [isOpenConfirmBegin, setIsOpenConfirmBegin] = useState(false);

  const handleClickMoveInput = useCallback(() => {
    setIsOpenConfirmBegin(false);

    delayPush(history, {
      pathname: "/input",
      state: { ...location.state },
    });
  }, [history, location]);

  const hancleClickInputButton = useCallback(() => {
    // サービス開始押下前に警告を表示
    if (beginEndBtn === beginendStatus.do) {
      setIsOpenConfirmBegin(true);
    } else {
      delayPush(history, {
        pathname: "/input",
        state: { ...location.state },
      });
    }
  }, [beginEndBtn, history, location]);

  const [openProfile, setOpenProfile] = useState(false);
  const handleClickProfile = () => {
    setOpenProfile(true);
    clearWatchPosition();
  };
  const handleCloseProfile = () => {
    setOpenProfile(false);

    if (shouldWatchPosition(position, options, selectedDate)) {
      setFlashMsg("現在地を取得中...");
      watchPosition();
    }
  };

  const [openHeed, setOpenHeed] = useState(false);
  const handleClickHeed = () => {
    setOpenHeed(true);
    clearWatchPosition();
  };
  const handleCloseHeed = () => {
    setOpenHeed(false);

    if (shouldWatchPosition(position, options, selectedDate)) {
      setFlashMsg("現在地を取得中...");
      watchPosition();
    }
  };

  const [kirokuTime, setKirokuTime] = useState({
    begin: kirokuBeginTime,
    end: kirokuEndTime,
  });

  const kirokuTimeRef = useRef({ begin: kirokuBeginTime, end: kirokuEndTime });

  const getKirokuTime = useCallback(() => {
    let begin = RegExp(/\d+:\d+/).exec(kirokuTime.begin) ?? "";
    let end = RegExp(/\d+:\d+/).exec(kirokuTime.end) ?? "";

    if (!begin && !end) {
      return "";
    }

    return `${begin}-${end}`;
  }, [kirokuTime.begin, kirokuTime.end]);

  return (
    <>
      <TopBar />

      {isIllegalOparation(location) ? null : (
        <div className={classes.root}>
          <Card className={classes.card} elevation={2}>
            <CardActionArea
              className={classes.cardActionArea}
              onClick={handleClickProfile}
            >
              <div className={classes.cardLeft}>
                <Typography variant="subtitle1">{selectedName}</Typography>

                <div className={classes.cardContent}>
                  <Typography color="textSecondary" variant="subtitle2">
                    {`${get日付(selectedDate)} ${timeRange}`}
                  </Typography>
                  <Typography color="textSecondary" variant="subtitle2">
                    {serviceName}
                  </Typography>
                </div>

                <Divider className={classes.dashed} />

                <div className={classes.chips}>
                  <Chip
                    icon={
                      <CheckIcon
                        className={
                          checkDoneItem(state) ? classes.doneCheck : null
                        }
                        fontSize="small"
                      />
                    }
                    label={checkDoneItem(state) ? "実施済" : "未実施"}
                    className={
                      checkDoneItem(state) ? classes.doneChip : classes.chip
                    }
                    size="small"
                  />

                  <Typography
                    color="primary"
                    variant="subtitle2"
                    className={classes.realTime}
                  >
                    {getKirokuTime()}
                  </Typography>
                </div>
              </div>

              <div>
                <ArrowRightIcon color="action" />
              </div>
            </CardActionArea>
          </Card>

          <Button
            color="primary"
            variant="contained"
            className={classes.inputButton}
            onClick={hancleClickInputButton}
          >
            実施記録を入力する
          </Button>

          <Button
            color="primary"
            variant="contained"
            className={classes.inputButton}
            onClick={handleClickHeed}
          >
            留意事項
          </Button>

          <div className={classes.flashMsg}>
            <Typography variant="subtitle2">{flashMsg}</Typography>
          </div>

          <div className={classes.serviceButtons}>
            <Button
              color={getBeginButtonColor()}
              variant="contained"
              className={classes.serviceButton}
              onClick={handleClickBeginService}
              disabled={isDisabledBtn || isDisabledBegin()}
            >
              {getBeginButtonText()}
            </Button>

            <Button
              color={getEndButtonColor()}
              variant="contained"
              className={classes.serviceButton}
              onClick={handleClickEndService}
              disabled={isDisabledBtn || isDisabledEnd()}
            >
              {getEndButtonText()}
            </Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={isOpenConfirm}
        onClose={() => setIsOpenConfirm(false)}
        okLabel={`${beginEndBtn === beginendStatus.do ? "開始" : "終了"}する`}
        onOk={() =>
          handleClickOk(
            beginEndBtn === beginendStatus.do
              ? beginServiceWrap
              : endServiceWrap
          )
        }
      >
        {`サービスを${
          beginEndBtn === beginendStatus.do ? "開始" : "終了"
        }しますか？`}
      </ConfirmDialog>

      <ConfirmDialog
        open={isOpenConfirmRevoke}
        onClose={() => setIsOpenConfirmRevoke(false)}
        okLabel="取り消す"
        onOk={handleClickRevoke}
        isDestructive
      >
        {`サービス${
          beginEndBtn === beginendStatus.doing ? "開始" : "終了"
        }処理を取り消しますか？\n※この操作はやり直しできません`}
      </ConfirmDialog>

      <ConfirmDialog
        open={isOpenConfirmBegin}
        onClose={() => setIsOpenConfirmBegin(false)}
        okLabel="移動する"
        onOk={handleClickMoveInput}
      >
        {"開始ボタンを押していません\n入力画面に移動してよろしいですか？"}
      </ConfirmDialog>

      <AlertDialog open={isOpenResult} onClose={handleCloseResult}>
        {resultMsgRef.current}
      </AlertDialog>

      <ErrorDialog
        open={isOpenError}
        onClose={handleCloseError}
        error={errorRef.current}
      />

      <Loading open={isLoading} />

      <TabDialog
        open={openProfile}
        onClose={handleCloseProfile}
        labels={["利用者情報"]}
        components={[Customer]}
      />

      <TabDialog
        open={openHeed}
        onClose={handleCloseHeed}
        labels={["連絡事項", "指示", "手順書"]}
        components={[Note, Order, Manual]}
      />
    </>
  );
}
